import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { LocalNotifications } from '@capacitor/local-notifications';
import { TenantService } from './tenant.service';

/**
 * Push notifications for Android AND iOS, both delivered through Firebase (FCM).
 * - Android and iOS both get an FCM token.
 * - The backend registers every device as FCM v1 in Azure Notification Hubs.
 * - Firebase delivers to Apple using the APNs key uploaded in the Firebase console.
 */
@Injectable({ providedIn: 'root' })
export class PushNotificationService {

  private readonly pushEnabled = true;

  // Android notification channel (Android 8+)
  private readonly CHANNEL_ID = 'default';

  // Listeners are added once per app session; the latest logged-in user is kept here
  private listenersAdded = false;
  private listenerHandles: PluginListenerHandle[] = [];
  private currentUserId: string | null = null;
  private deviceToken: string | null = null;

  constructor(
    private router: Router,
    private http: HttpClient,
    private zone: NgZone,
    private tenantService: TenantService
  ) {}

  /**
   * Call this immediately after a successful login on native platforms
   * (and on app start when a user is already logged in).
   */
  async initialize(userId: string): Promise<void> {
    if (!Capacitor.isNativePlatform() || !this.pushEnabled) return;

    this.currentUserId = String(userId);

    try {
      if (Capacitor.getPlatform() === 'android') {
        await FirebaseMessaging.createChannel({
          id: this.CHANNEL_ID,
          name: 'General',
          description: 'General notifications',
          importance: 5,
          visibility: 1
        });
      }

      await this.registerListeners();

      let permStatus = await FirebaseMessaging.checkPermissions();
      if (permStatus.receive === 'prompt' || permStatus.receive === 'prompt-with-rationale') {
        permStatus = await FirebaseMessaging.requestPermissions();
      }

      if (permStatus.receive !== 'granted') {
        console.warn('PushNotifications: permission not granted');
        return;
      }

      // Get the FCM token (works the same on Android and iOS)
      const { token } = await FirebaseMessaging.getToken();
      if (token) {
        console.log('PushNotifications: token received');
        this.deviceToken = token;
        this.sendTokenToBackend(token);
      }
    } catch (err) {
      console.error('PushNotifications: initialization failed', err);
    }
  }

  private async registerListeners(): Promise<void> {
    if (this.listenersAdded) return;
    this.listenersAdded = true;

    // Firebase issued a NEW token (e.g. after reinstall): register it again
    this.listenerHandles.push(
      await FirebaseMessaging.addListener('tokenReceived', event => {
        if (event.token && event.token !== this.deviceToken) {
          this.deviceToken = event.token;
          this.sendTokenToBackend(event.token);
        }
      })
    );

    // Notification arrives while the app is OPEN.
    // iOS shows the banner itself (presentationOptions in capacitor.config.ts).
    // Android does not, so we show it as a local notification.
    this.listenerHandles.push(
      await FirebaseMessaging.addListener('notificationReceived', async event => {
        const notification = event.notification;
        console.log('Foreground notification:', notification.title, notification.body);

        if (Capacitor.getPlatform() !== 'android') return;

        const data: any = notification.data ?? {};
        try {
          await LocalNotifications.schedule({
            notifications: [{
              id: Math.floor(Date.now() % 2147483647),
              title: notification.title ?? data.title ?? 'Notification',
              body: notification.body ?? data.body ?? '',
              channelId: this.CHANNEL_ID,
              extra: data
            }]
          });
        } catch (err) {
          console.error('Could not show foreground notification', err);
        }
      })
    );

    // User tapped a push notification (app was closed or in the background)
    this.listenerHandles.push(
      await FirebaseMessaging.addListener('notificationActionPerformed', event => {
        const data: any = event.notification?.data ?? {};
        this.openRoute(data.route);
      })
    );

    // User tapped a notification that was shown while the app was open (Android)
    this.listenerHandles.push(
      await LocalNotifications.addListener('localNotificationActionPerformed', action => {
        this.openRoute(action.notification.extra?.route);
      })
    );
  }

  private openRoute(route?: string): void {
    if (!route) return;
    this.zone.run(() => this.router.navigateByUrl(route));
  }

  private sendTokenToBackend(token: string): void {
    const cfg = this.tenantService.getConfig();
    const userId = this.currentUserId;
    if (!cfg || !userId) return;

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    this.http.post(
      `${cfg.apiBaseUrl}/Notifications/RegisterDevice`,
      {
        DeviceToken: token,                 // an FCM token on BOTH platforms
        UserId: userId,
        MarketId: cfg.marketId,
        Platform: Capacitor.getPlatform()   // 'android' | 'ios' (info only; both register as FCM v1)
      },
      { headers }
    ).subscribe({
      next: () => console.log('Device token registered with backend successfully.'),
      error: (err) => console.error(
        `Failed to register device token. status=${err?.status} message=${err?.error?.message || err?.message}`
      )
    });
  }

  /**
   * Call on logout (BEFORE clearing the session) to remove this device
   * from the backend and stop receiving notifications on this device.
   */
  async unregister(userId: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;

    const cfg = this.tenantService.getConfig();
    if (cfg) {
      const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      this.http.post(
        `${cfg.apiBaseUrl}/Notifications/UnregisterDevice`,
        { UserId: String(userId) },
        { headers }
      ).subscribe();
    }

    this.currentUserId = null;
    this.deviceToken = null;

    try {
      await FirebaseMessaging.deleteToken();   // a fresh token is issued on the next login
    } catch (err) {
      console.warn('PushNotifications: could not delete token', err);
    }
  }

  /** Remove all delivered notifications from the notification tray. */
  async clearDeliveredNotifications(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    await FirebaseMessaging.removeAllDeliveredNotifications();
  }
}